import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useToggle } from "react-use";

import get from "lodash/get";
import clsx from "clsx";
import map from "lodash/map";
import intersectionWith from "lodash/intersectionWith";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import {
  QUERY_PROPERTY_COMPANY,
  QUERY_MEMBER_GROUPS,
  QUERY_GOOGLE_WORKSPACES,
  QUERY_GOOGLE_WORKSPACE_GROUPS,
  QUERY_GOOGLE_WORKSPACE_SYNC,
  QUERY_GOOGLE_WORKSPACE_SYNCS,
  QUERY_GOOGLE_WORKSPACE_ATTRIBUTES,
} from "../../../../config/graphql/query";

import {
  MUTATION_CREATE_GOOGLE_WORKSPACE_SYNC,
  MUTATION_DELETE_GOOGLE_WORKSPACE_SYNC,
  MUTATION_UPDATE_GOOGLE_WORKSPACE_SYNC,
} from "../../../../config/graphql/mutation";

import RoleBlock from "../../../../components/RoleBlock";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";
import { useCurrentClient } from "../../../../context/Client";

type FieldValues = {
  company?: Pick<ICompany, "id" | "title">;
  googleWorkspace?: Pick<IGoogleWorkspace, "id" | "email" | "title">;
  group?: IGoogleWorkspaceGroup;
  memberGroups?: IMemberGroup[];
  settings: IGoogleWorkspaceSyncSettings;
};

const useRouteParams = () =>
  useParams<{ companyId: string; integrationId?: string }>();

const useQueryCompany = () => {
  const { companyId } = useRouteParams();
  return useQuery<{ property: ICompany }>(QUERY_PROPERTY_COMPANY, {
    variables: { id: companyId },
  });
};

const BreadcrumbsCompany = () => {
  const { t } = useTranslation(["company"]);
  const { companyId } = useRouteParams();
  const { data: companyData } = useQueryCompany();

  return (
    <nav aria-label="breadcrumb">
      <ol className="breadcrumb my-3">
        <li className="breadcrumb-item">
          <Link to="/company">{t("company:nav.companies")}</Link>
        </li>
        <li className="breadcrumb-item">
          <Link to={`/company/${companyId}`}>
            {companyData?.property?.title ?? t("company:nav.company")}
          </Link>
        </li>
        <li className="breadcrumb-item">
          <Link to={`/company/${companyId}/integrations`}>
            {t("company:nav.integrations")}
          </Link>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          {t("company:nav.googleWorkspaceSync")}
        </li>
      </ol>
    </nav>
  );
};

const GoogleWorkspaceGroupSelect = () => {
  const { t } = useTranslation(["googleWorkspaceSync"]);
  const methods = useFormContext();
  const googleWorkspace = methods.watch("googleWorkspace");
  const [search, setSearch] = useState("");
  const [queryError, setQueryError] = useState<string>();

  const { data: groupsData, loading: isLoadingGroups } = useQuery<{
    fetchGoogleWorkspaceGroups: IGoogleWorkspaceGroup[];
  }>(QUERY_GOOGLE_WORKSPACE_GROUPS, {
    variables: {
      filter: { id: googleWorkspace?.id, name: search },
    },
    skip: !googleWorkspace?.id,
    onError: (error: any) => {
      const errorMessage =
        error?.networkError?.result?.errors?.[0]?.message ?? error?.message;
      setQueryError(errorMessage);
    },
  });

  return (
    <div className="form-group">
      <label>{t("googleWorkspaceSync:input:label.group")}</label>
      <Controller
        name="group"
        render={({ field: { onChange, ...props } }) => {
          const options = groupsData?.fetchGoogleWorkspaceGroups ?? [];
          return (
            <>
              <Select
                getOptionLabel={({ name, email }) =>
                  [name, email].filter(Boolean).join(" - ")
                }
                getOptionValue={({ id }) => id}
                options={options}
                isDisabled={!googleWorkspace}
                isClearable
                isLoading={isLoadingGroups}
                onChange={onChange}
                onInputChange={(value) => setSearch(value)}
                {...props}
              />
              {!!queryError && (
                <small className="text-muted">{queryError}</small>
              )}
            </>
          );
        }}
      />
      <small className="form-text text-muted small">
        {t("googleWorkspaceSync:input:helper.group")}
      </small>
    </div>
  );
};

const GoogleWorkspaceNfcSync = () => {
  const { t } = useTranslation(["googleWorkspaceSync"]);
  const methods = useFormContext();
  const googleWorkspace = methods.watch("googleWorkspace");
  const nfcEnabled = methods.watch("settings.syncFields.nfc.enabled");
  const [queryError, setQueryError] = useState<string>();

  const { data: attributesData, loading: isLoadingAttributes } = useQuery<{
    fetchGoogleWorkspaceAttributes: string[];
  }>(QUERY_GOOGLE_WORKSPACE_ATTRIBUTES, {
    variables: { id: googleWorkspace?.id },
    skip: !googleWorkspace?.id,
    onError: (error: any) => {
      const errorMessage =
        error?.networkError?.result?.errors?.[0]?.message ?? error?.message;
      setQueryError(errorMessage);
    },
  });

  return (
    <div className="form-group">
      <Controller
        name="settings.syncFields.nfc.enabled"
        defaultValue={false}
        render={({
          field: { value, name, onChange },
          fieldState: { error },
        }) => (
          <div className="form-group form-check mb-2">
            <input
              id={name}
              type="checkbox"
              className="form-check-input"
              checked={value}
              onChange={onChange}
            />
            <label className="form-check-label user-select-none" htmlFor={name}>
              {t("googleWorkspaceSync:input:label.settings.nfc.enabled")}
            </label>
            {!!error && <div className="invalid-feedback">{error.message}</div>}
          </div>
        )}
      />
      {nfcEnabled && (
        <>
          <label>
            {t("googleWorkspaceSync:input:label.settings.nfc.title")}
          </label>
          {!!queryError && (
            <small className="form-text text-muted mb-1">{queryError}</small>
          )}
          <Controller
            name="settings.syncFields.nfc.attribute"
            rules={{
              validate: (value) => {
                if (nfcEnabled && !value) {
                  return t(
                    "googleWorkspaceSync:input:error.nfcAttribute.required",
                  );
                }
                return true;
              },
            }}
            render={({
              field: { onChange, value, ...props },
              fieldState: { error },
            }) => {
              const options = (
                attributesData?.fetchGoogleWorkspaceAttributes ?? []
              ).map((attr) => ({ id: attr, displayName: attr }));

              return (
                <>
                  <Select
                    options={options}
                    getOptionLabel={(opt) => opt.displayName}
                    getOptionValue={(opt) => opt.id}
                    onChange={(selected) => onChange(selected?.id || null)}
                    value={options.find((opt) => opt.id === value) || null}
                    isDisabled={!googleWorkspace || !nfcEnabled}
                    isClearable
                    isLoading={isLoadingAttributes}
                    className={clsx({ "is-invalid": !!error })}
                    styles={reactSelectCustomStyles(!!error)}
                    {...props}
                  />
                  {!!error && (
                    <div className="invalid-feedback">{error?.message}</div>
                  )}
                </>
              );
            }}
          />
        </>
      )}
    </div>
  );
};

const GoogleWorkspaceSync = React.memo(() => {
  const history = useHistory();
  const { t } = useTranslation(["googleWorkspaceSync"]);

  const [show, setShow] = useToggle(false);
  const [keepEmployees, setKeepEmployees] = useState<boolean>(false);

  const { integrationId, companyId } = useRouteParams();
  const currentClient = useCurrentClient();
  const nfcEnabled = get(currentClient, "preferences.nfc.enabled");

  const methods = useForm<FieldValues>();
  const { data: companyData } = useQueryCompany();

  const { data: groupsData } = useQuery<{ memberGroups: IMemberGroup[] }>(
    QUERY_MEMBER_GROUPS,
    { fetchPolicy: "network-only" },
  );

  const { data: googleWorkspaceSyncData } = useQuery<{
    googleWorkspaceSync: IGoogleWorkspaceSync;
  }>(QUERY_GOOGLE_WORKSPACE_SYNC, {
    variables: { id: integrationId },
    skip: !integrationId,
    onError: () => {
      history.replace(`/company/${companyId}/integrations/googleworkspace`);
    },
  });

  const phoneEnabled = methods.watch("settings.syncFields.phone.enabled");

  const { data: googleWorkspacesData, loading: isLoadingGoogleWorkspaces } =
    useQuery<{ googleWorkspaces: IGoogleWorkspace[] }>(QUERY_GOOGLE_WORKSPACES);

  // Pre-fill company on create
  useEffect(() => {
    if (companyData?.property && !integrationId) {
      methods.setValue("company", companyData.property);
    }
  }, [companyData, methods]);

  // Pre-fill first Google Workspace account on create
  useEffect(() => {
    if (googleWorkspacesData?.googleWorkspaces && !integrationId) {
      methods.setValue(
        "googleWorkspace",
        googleWorkspacesData.googleWorkspaces[0],
      );
    }
  }, [googleWorkspacesData?.googleWorkspaces, integrationId, methods]);

  // Populate form on edit
  useEffect(() => {
    if (googleWorkspaceSyncData) {
      methods.reset(googleWorkspaceSyncData.googleWorkspaceSync);
    }
  }, [googleWorkspaceSyncData, methods]);

  const [onCreate, { loading: isCreating }] = useMutation<{
    createGoogleWorkspaceSync: IGoogleWorkspaceSync;
  }>(MUTATION_CREATE_GOOGLE_WORKSPACE_SYNC, {
    refetchQueries: [{ query: QUERY_GOOGLE_WORKSPACE_SYNCS }],
  });

  const [onUpdate, { loading: isUpdating }] = useMutation<{
    updateGoogleWorkspaceSync: IGoogleWorkspaceSync;
  }>(MUTATION_UPDATE_GOOGLE_WORKSPACE_SYNC, {
    refetchQueries: [{ query: QUERY_GOOGLE_WORKSPACE_SYNCS }],
  });

  const [onDelete, { loading: isDeleting }] = useMutation<{
    deleteGoogleWorkspaceSync: IGoogleWorkspaceSync;
  }>(MUTATION_DELETE_GOOGLE_WORKSPACE_SYNC, {
    refetchQueries: [{ query: QUERY_GOOGLE_WORKSPACE_SYNCS }],
    awaitRefetchQueries: true,
  });

  const onSubmit = (variables: FieldValues) => {
    // Shared settings payload builder
    const settingsPayload = {
      syncFields: {
        phone: {
          enabled: variables?.settings?.syncFields?.phone?.enabled ?? false,
          type:
            variables?.settings?.syncFields?.phone?.type?.map((t) => t) ?? [],
        },
        nfc: {
          enabled: variables?.settings?.syncFields?.nfc?.enabled ?? false,
          attribute: variables?.settings?.syncFields?.nfc?.attribute ?? null,
        },
        skipAccess: variables?.settings?.syncFields?.skipAccess,
      },
    };

    if (integrationId) {
      return onUpdate({
        variables: {
          input: {
            id: integrationId,
            groupId: variables.group?.id ?? null,
            memberGroups: variables.memberGroups?.map(({ id }) => id) ?? [],
            settings: settingsPayload,
          },
        },
      })
        .then(() => {
          toast.success<string>(t("googleWorkspaceSync:toast.updateSuccess"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    }

    return onCreate({
      variables: {
        input: {
          googleWorkspace: variables?.googleWorkspace?.id,
          company: variables?.company?.id,
          groupId: variables.group?.id,
          memberGroups: variables.memberGroups?.map(({ id }) => id) ?? [],
          settings: settingsPayload,
        },
      },
    })
      .then(({ data }) => {
        toast.success<string>(t("googleWorkspaceSync:toast.createSuccess"));
        return history.push(
          `googleworkspace/${data?.createGoogleWorkspaceSync?.id}`,
        );
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );
      });
  };

  const onBeforeRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setShow(true);
    },
    [setShow],
  );

  const onRemoveGoogleWorkspaceSync = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      return onDelete({
        variables: {
          input: { id: integrationId, keepEmployees },
        },
      })
        .then(() => {
          history.replace(`/company/${companyId}/integrations`);
          toast.success<string>(t("company:information.toast.deleted"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [history, onDelete, integrationId, keepEmployees, companyId, t],
  );

  const phoneTypes = useMemo(
    () => [
      {
        title: t("googleWorkspaceSync:input:option.phoneType.work"),
        type: "work",
      },
      {
        title: t("googleWorkspaceSync:input:option.phoneType.mobile"),
        type: "mobile",
      },
      {
        title: t("googleWorkspaceSync:input:option.phoneType.home"),
        type: "home",
      },
    ],
    [t],
  );

  return (
    <div className="container-fluid">
      <BreadcrumbsCompany />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-12 d-flex">
            <div className="col-lg-4 col-md-6 col-sm-12 row mr-3">
              <h6>{t("googleWorkspaceSync:settings.details")}</h6>
              <div className="w-100">
                <div className="form-group">
                  <label htmlFor="title">
                    {t("googleWorkspaceSync:input:label.company")}
                  </label>
                  <Controller
                    name="company"
                    render={({ field: { onChange, ...props } }) => (
                      <Select
                        getOptionLabel={({ title }) => title}
                        getOptionValue={({ id }) => id}
                        options={[companyData?.property]}
                        onChange={(value) => onChange(value)}
                        isDisabled
                        {...props}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: t(
                          "googleWorkspaceSync:input:error.company.required",
                        ),
                      },
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="googleWorkspace">
                    {t("googleWorkspaceSync:input:label.googleWorkspace")}
                  </label>
                  <Controller
                    name="googleWorkspace"
                    render={({ field: { onChange, ...props } }) => (
                      <Select
                        getOptionLabel={({ name, email }) =>
                          [name, email].filter(Boolean).join(" - ")
                        }
                        getOptionValue={({ id }) => id}
                        isDisabled={!!integrationId}
                        isLoading={isLoadingGoogleWorkspaces}
                        options={googleWorkspacesData?.googleWorkspaces ?? []}
                        onChange={(value) => onChange(value)}
                        {...props}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: t(
                          "googleWorkspaceSync:input:error.googleWorkspace.required",
                        ),
                      },
                    }}
                  />
                </div>

                <GoogleWorkspaceGroupSelect />

                <div className="form-group">
                  <label htmlFor="memberGroups">
                    {t("googleWorkspaceSync:input:label.employeeGroup")}
                  </label>
                  <Controller
                    name="memberGroups"
                    render={({ field: { onChange, ...props } }) => (
                      <Select
                        getOptionLabel={({ title }) => title}
                        getOptionValue={({ id }) => id}
                        options={groupsData?.memberGroups ?? []}
                        onChange={(value) => onChange(value)}
                        isMulti
                        closeMenuOnSelect={false}
                        {...props}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12 row">
              <div className="w-100">
                <h6 className="mb-3">
                  {t("googleWorkspaceSync:settings.title")}
                </h6>

                <div className="w-100">
                  <div className="form-group">
                    <Controller
                      name="settings.syncFields.skipAccess"
                      defaultValue={false}
                      render={({
                        field: { value, name, onChange },
                        fieldState: { error },
                      }) => (
                        <div className="form-group form-check mb-2">
                          <input
                            id={name}
                            type="checkbox"
                            className="form-check-input"
                            checked={value}
                            onChange={onChange}
                          />
                          <label
                            className="form-check-label user-select-none"
                            htmlFor={name}
                          >
                            {t(
                              "googleWorkspaceSync:input:label.settings.skipAccess",
                            )}
                          </label>
                          {!!error && (
                            <div className="invalid-feedback">
                              {error.message}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className="w-100">
                  <div className="form-group">
                    <h6 className="mb-3">
                      {t("googleWorkspaceSync:input:label.syncPropertiesTitle")}
                    </h6>

                    <Controller
                      name="settings.syncFields.phone.enabled"
                      defaultValue={false}
                      render={({
                        field: { value, name, onChange },
                        fieldState: { error },
                      }) => (
                        <div className="form-group form-check mb-2">
                          <input
                            id={name}
                            type="checkbox"
                            className="form-check-input"
                            checked={value}
                            onChange={onChange}
                          />
                          <label
                            className="form-check-label user-select-none"
                            htmlFor={name}
                          >
                            {t(
                              "googleWorkspaceSync:input:label.settings.phone.enabled",
                            )}
                          </label>
                          {!!error && (
                            <div className="invalid-feedback">
                              {error.message}
                            </div>
                          )}
                        </div>
                      )}
                    />

                    {phoneEnabled && (
                      <>
                        <Controller
                          name="settings.syncFields.phone.type"
                          render={({
                            field: { onChange, value: inputValue, ...props },
                          }) => {
                            const value = intersectionWith(
                              phoneTypes,
                              inputValue,
                              (option, val) => option.type === val,
                            );
                            return (
                              <Select
                                getOptionLabel={({ title }) => title}
                                getOptionValue={({ type }) => type}
                                options={phoneTypes}
                                onChange={(value) =>
                                  onChange(map(value, (v) => v.type))
                                }
                                isMulti
                                value={value}
                                closeMenuOnSelect={false}
                                isDisabled={!phoneEnabled}
                                {...props}
                              />
                            );
                          }}
                        />
                        <small className="form-text text-muted">
                          {t("googleWorkspaceSync:settings.phone.subtitle")}
                        </small>
                      </>
                    )}
                  </div>
                </div>

                {nfcEnabled && <GoogleWorkspaceNfcSync />}

                <small className="form-text text-muted small">
                  {t("googleWorkspaceSync:settings.subtitle")}
                </small>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <input
                type="submit"
                className="btn btn-primary"
                disabled={isCreating || isUpdating}
                value={t("googleWorkspaceSync:button.create")}
              />
              <RoleBlock roles={["ADMIN", "CLIENT_ADMIN"]}>
                {integrationId && (
                  <button
                    onClick={onBeforeRemove}
                    className="btn btn-danger ml-3"
                    disabled={isUpdating || isDeleting}
                  >
                    {t("common:delete")}
                  </button>
                )}
              </RoleBlock>
            </div>
          </div>
        </form>
      </FormProvider>

      <Modal show={show} onHide={setShow} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("company:integrations.googleWorkspace.modal.delete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group form-check mb-3">
            <input
              id="keepEmployees"
              type="checkbox"
              className="form-check-input"
              checked={keepEmployees}
              onChange={(e) => setKeepEmployees(e.target.checked)}
            />
            <label
              className="form-check-label user-select-none"
              htmlFor="keepEmployees"
            >
              {t(
                "company:integrations.googleWorkspace.modal.delete.keepEmployees",
              )}
            </label>
          </div>
          <div>
            {t("company:integrations.googleWorkspace.modal.delete.body")}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={setShow}>
            {t(
              "company:integrations.googleWorkspace.modal.delete.button.dismiss",
            )}
          </Button>
          <Button variant="danger" onClick={onRemoveGoogleWorkspaceSync}>
            {t(
              "company:integrations.googleWorkspace.modal.delete.button.submit",
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default GoogleWorkspaceSync;
